import { useMutation, useQueryClient } from 'react-query';

import { postV2ApiOrganisationIdSettings } from 'src/client/spendesk-api';
import { useOrganisationId } from 'src/core/modules/app/hooks/useOrganisationId';
import { apiUrl } from 'src/core/utils/api';

type CreateCostCenterData = {
  type: 'singleEntity' | 'multiEntity';
  name: string;
  ownerId: string;
  viewerIds: string[];
  companyIds: string[];
  autoAssignFutureEntities: boolean;
};

const createMultiEntityCostCenter = async (
  organisationId: string,
  data: CreateCostCenterData,
): Promise<void> => {
  const response = await postV2ApiOrganisationIdSettings({
    path: {
      organisationId,
    },
    body: {
      type: 'costCenter',
      data: {
        name: data.name,
        ownerId: data.ownerId,
        viewerIds: data.viewerIds,
      },
      autoAssignFutureEntities: data.autoAssignFutureEntities,
      companyIds: data.companyIds,
    },
  });

  if (response.statusCode !== 200) {
    throw new Error('Failed to create cost center');
  }
};

export const createSingleEntityCostCenter = async (
  companyId: string,
  data: CreateCostCenterData,
): Promise<void> => {
  const response = await fetch(apiUrl('/cost-centers', companyId), {
    method: 'POST',
    credentials: 'include',
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error('Impossible to create a cost center');
  }
  return response.json();
};

export const useCreateOrganisationCostCenter = () => {
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();

  return useMutation(
    (data: CreateCostCenterData): Promise<void> =>
      data.type === 'multiEntity' || data.autoAssignFutureEntities
        ? createMultiEntityCostCenter(organisationId, data)
        : createSingleEntityCostCenter(data.companyIds[0], data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['settings', organisationId]);
      },
    },
  );
};
